<template>
    <div class="Tabs-component">
        <div class="Tabs-component-header">
            <div v-for="tab in Tabs" class="Tabs-component-header-item" :class="{ [tab.class]: tab.class, 'selected': ( activeTabID === tab.id ) || tab?.class?.includes('selected') }" :key="tab.id" @click="handleTabClick( tab )">
                <template v-if="tab.slots.before">
                    <component :is="tab.slots.before" />
                </template>
                <template v-if="tab?.before">
                    <span v-if="typeof tab.before === 'string'" v-html="tab.before"></span>
                </template>
                {{tab.title}}
                <div class="Tab-remove">
                    <i v-if="tab.removeable" class="isax isax-add" @click.stop="handleTabRemove( tab.id )"></i>
                </div>
                <template v-if="tab.slots.after">
                    <component :is="tab.slots.after" />
                </template>
                <template v-if="tab?.after">
                    <span v-if="typeof tab.after === 'string'" v-html="tab.after"></span>
                </template>
            </div>
        </div>
        <div class="Tabs-component-content">
            <template v-for="slot of Contents">
                <template v-if="renderOnlyActiveTab && renderedTabIDs.has( slot.props.id ) && activeTabID === slot.props.id">
                    <component :key="slot.props.id" :is="slot" />
                </template>
                <template v-else-if="!renderOnlyActiveTab && renderedTabIDs.has( slot.props.id )">
                        <component v-show="activeTabID === slot.props.id" :key="slot.props.id" :is="slot" />
                </template>
            </template>
        </div>
    </div>
</template>

<script setup>
import { useSlots, getCurrentInstance, reactive, ref, toRef, watch, nextTick } from 'vue';

const app = getCurrentInstance();

const { $router } = app.appContext.config.globalProperties;

const props = defineProps(
{
    renderOnlyActiveTab : { type: Boolean, default: true },
    name                : { type: String, default: undefined },
    params              : { type: Object, default: null },
    query               : { type: Object, default: null },
});

const emits = defineEmits(['on:tab-remove']);

const slots = useSlots();

const Tabs            = reactive( [] );
const Contents        = reactive( [] );
const renderedTabIDs  = reactive( new Set() );
const activeTabID     = ref( null );

const handleTabClick = async ( tab ) =>
{
    let $_routerObj = {};

    if( props.query )
    {
        let pushQuery = {};

        if( props.query?.follow?.includes( '*' ) )
        {
            pushQuery = { ...$router.currentRoute.value.query, [ props.query.key ]: tab.id };
        }
        else
        {
            pushQuery[ props.query.key ] = tab.id;

            for( let key in $router.currentRoute.value.query )
            {
                if( props.query?.follow?.includes( key ) )
                {
                    pushQuery[key] = $router.currentRoute.value.query[key];
                }
            }
        }

        $_routerObj.query = pushQuery;
    }

    if( props.name )   { $_routerObj.name = props.name; }
    if( props.params ) { $_routerObj.params = props.params; }

    await $router.replace( $_routerObj );

    for( let $_tab of Tabs ) { $_tab.active = $_tab.id === tab.id; }

    activeTabID.value = tab.id;

    renderedTabIDs.add( activeTabID.value );
}

const handleTabRemove = ( tabID ) =>
{
    Tabs.splice( Tabs.findIndex( tab => tab.id === tabID ), 1 );
    Contents.splice( Contents.findIndex( tab => tab.props.id === tabID ), 1 );
    renderedTabIDs.delete( tabID );

    if( activeTabID.value === tabID )
    {
        activeTabID.value = Tabs[0].id;
    }

    emits('on:tab-remove', tabID );
}

const getActiveSlot = ( slot ) =>
{
    if( slot.props.hasOwnProperty('active') )
    {
        return slot.props.active;
    }
    else if( props.query )
    {
        if( $router.currentRoute.value.query[ props.query.key ] )
        {
            return $router.currentRoute.value.query[ props.query.key ] === slot.props.id;
        }
        else
        {
            return props.query.default === slot.props.id;
        }
    }

    return false;
}

const getSlotProps = ( slot ) =>
{
    if( !slot.props ){ return null; }

    return (
    {
        id         : toRef( slot.props, 'id' ),
        title      : toRef( slot.props, 'title' ),
        class      : toRef( slot.props, 'class' ),
        before     : toRef( slot.props, 'before' ),
        after      : toRef( slot.props, 'after' ),
        removeable : toRef( slot.props, 'removeable' ),
        active     : getActiveSlot( slot ),
        slots      : typeof slot.children === 'object' ? slot.children : {}
    });
}

const getTabs = () =>
{
    for( const slot of slots.default() )
    {
        if( typeof slot.type === 'symbol' )
        {
            if( Array.isArray( slot.children ) )
            {
                for( const children of slot.children )
                {
                    const slotProps = getSlotProps( children );

                    if( slotProps && !Tabs.find( t => t.id === slotProps.id.value ) )
                    {
                        Contents.push( children );

                        Tabs.push( slotProps );
                    }
                    else
                    {
                        let tab = Tabs.find( t => t.id === slotProps.id.value );

                        Object.assign( tab, slotProps );
                    }
                }
            }
        }
        else
        {
            const slotProps = getSlotProps( slot );

            if( slotProps && !Tabs.find( t => t.id === slotProps.id.value ) )
            {
                Contents.push( slot );

                Tabs.push( slotProps );
            }
            else
            {
                let tab = Tabs.find( t => t.id === slotProps.id.value );

                Object.assign( tab, slotProps );
            }
        }
    }

    activeTabID.value = Tabs.find( tab => tab.active )?.id ?? Tabs[0].id;

    renderedTabIDs.add( activeTabID.value );
}

if( props.query )
{
    watch( () => [ $router.currentRoute.value.query ], ([ query ]) =>
    {
        let newActiveTab;

        if( query.hasOwnProperty( props.query.key ) && ( newActiveTab = Tabs.find( t => t.id === query[props.query.key] ) ) )
        {
            for( let tab of Tabs ) { tab.active = tab.id === newActiveTab.id; }

            activeTabID.value = newActiveTab.id;

            renderedTabIDs.add( activeTabID.value );
        }
    });
}

watch( () => [ slots.default() ], ([ newSlot ]) => { nextTick( () => getTabs() ) }, { immediate: true });

defineExpose({ open: ( tabID ) => handleTabClick({ id: tabID }) });
</script>

<style lang="scss">
body
{
    &[data-theme="light"]
    {
        .Tabs-component
        {
            --tab-color: var(--light-text-color-secondary);
            --tab-border-color: var(--layout-border-color);
            --tab-background-color: transparent;

            --tab-hover-color: var(--primary-colors-blue-60);
            --tab-hover-border-color: var(--layout-border-color);
            --tab-hover-background-color: transparent;

            --tab-selected-color: var(--primary-text-color);
            --tab-selected-border-color: var(--primary-colors-blue-60);
            --tab-selected-background-color: transparent;

            --tab-disabled-color: var(--disabled-text-color);
            --tab-disabled-border-color: var(--layout-border-color);
            --tab-disabled-background-color: transparent;

            --tab--remove-background-color: var(--layout-primary-background-color);

            --tab--hover-remove-color: var(--error-text-color);
            --tab--hover-remove-background-color: var(--error-colors-error-10);
        }
    }

    &[data-theme="dark"]
    {
        .Tabs-component
        {
            --tab-color: var(--light-text-color-secondary);
            --tab-border-color: var(--layout-border-color);
            --tab-background-color: transparent;

            --tab-hover-color: var(--primary-colors-blue-40);
            --tab-hover-border-color: var(--layout-border-color);
            --tab-hover-background-color: transparent;

            --tab-selected-color: var(--primary-text-color);
            --tab-selected-border-color: var(--primary-colors-blue-40);
            --tab-selected-background-color: transparent;

            --tab-disabled-color: var(--disabled-text-color);
            --tab-disabled-border-color: var(--layout-border-color);
            --tab-disabled-background-color: transparent;

            --tab--remove-background-color: var(--layout-primary-background-color);

            --tab--hover-remove-color: var(--error-text-color);
            --tab--hover-remove-background-color: var(--layout-primary-background-color);
        }
    }
}

.Tabs-component
{
    position: relative;

    height: 100%;
    width: 100%;

    &.lg
    {
        --tab-height: #{px(48)};

        .Tabs-component-header
        {
            .Tabs-component-header-item
            {
                @include font-body-sm-regular;

                height: var(--tab-height);

                padding: px(12) px(24);
            }
        }
    }

    &.sm
    {
        --tab-height: #{px(36)};

        .Tabs-component-header
        {
            .Tabs-component-header-item
            {
                @include font-caption-md-regular;

                height: var(--tab-height);

                padding: px(12) px(24);
            }
        }
    }

    .Tabs-component-header
    {
        position: relative;

        display: flex;
        flex-wrap: nowrap;
        flex-direction: row;
        align-items: flex-start;

        width: 100%;
        height: var(--tab-height);

        user-select: none;
        cursor: pointer;

        border-bottom: 1px solid var(--tab-border-color);

        .Tabs-component-header-item
        {
            position: relative;

            display: flex;
            align-items: center;
            justify-content: center;

            gap: px(4);

            white-space: nowrap;
            cursor: pointer;

            color: var(--tab-color);
            background-color: var(--tab-background-color);

            > .Tab-remove
            {
                display: flex;
                justify-content: center;
                align-items: center;

                background: var(--tab--remove-background-color);

                border-radius: 50%;

                &:hover
                {
                    color: var(--tab--hover-remove-color);
                    background: var(--tab--hover-remove-background-color);
                }

                > i
                {
                    display: flex;
                    align-items: center;
                    justify-content: center;

                    width: px(20);
                    height: px(20);
                    font-size: px(20);

                    margin-left: -1px;

                    transform: rotate( 45deg );
                }
            }

            &:after
            {
                content: '';

                position: absolute;

                bottom: 0;
                width: 100%;

                border-bottom: 1px solid var(--tab-border-color);
            }

            &.hovered,
            &:hover
            {
                color: var(--tab-hover-color);
                background-color: var(--tab-hover-background-color);

                &:after
                {
                    border-bottom: 1px solid var(--tab-hover-border-color);
                }
            }

            &.selected:not(.hovered):not(.disabled)
            {
                color: var(--tab-selected-color);
                background-color: var(--tab-selected-background-color);

                text-shadow: 0 0 0 var(--tab-selected-color);

                &:after
                {
                    border-bottom: 2px solid var(--tab-selected-border-color);
                }
            }

            &.disabled
            {
                cursor: not-allowed;

                color: var(--tab-disabled-color);
                background-color: var(--tab-disabled-background-color);

                &:after
                {
                    border-bottom: 1px solid var(--tab-disabled-border-color);
                }
            }
        }
    }

    .Tabs-component-content
    {
        position: relative;

        height: calc( 100% - var( --tab-height ) );
    }
}
</style>